import React, { useState, useMemo } from "react";
import {
  Container,
  Typography,
  Paper,
  Box,
  Chip,
  Button,
  Grid,
  Card,
  CardContent,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PersonIcon from "@mui/icons-material/Person";
import { useTranslation } from "react-i18next";
import { getTextByLanguage } from "../api/text";
import { SERVER_URL } from "../api";

const TIPOS_ACTIVIDAD_COLORS = {
  conferencia: "primary",
  taller: "secondary",
  networking: "success",
  descanso: "default",
  comida: "warning",
  otro: "info",
};

const TIPOS_ACTIVIDAD_LABELS = {
  conferencia: "Conferencia",
  taller: "Taller",
  networking: "Networking",
  descanso: "Descanso",
  comida: "Comida",
  otro: "Otro",
};

const AgendaContent = ({ agendaConfig, eventData, onNavigateToRatings, contenidoIntermedio }) => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language || "es";
  const [filtroTipo, setFiltroTipo] = useState("todos");
  const [diaSeleccionado, setDiaSeleccionado] = useState(null);

  const estilos = contenidoIntermedio?.estilos || {};
  const primaryColor = estilos.colorPrimario || "#66bc5f";

  // Obtener texto traducido
  const getTranslatedText = (textObj) => {
    if (!textObj) return "";
    if (typeof textObj === "string") return textObj;
    return textObj[currentLang] || textObj["es"] || Object.values(textObj)[0] || "";
  };

  const dias = agendaConfig?.dias || [];
  const formatoVisualizacion = agendaConfig?.formatoVisualizacion || "timeline";

  // Obtener todas las actividades de todos los días
  const todasActividades = useMemo(() => {
    const actividades = [];
    dias.forEach((dia) => {
      (dia.actividades || []).forEach((actividad) => {
        if (actividad.activo !== false) {
          actividades.push({
            ...actividad,
            diaFecha: dia.fecha,
            diaNombre: getTranslatedText(dia.nombre),
          });
        }
      });
    });
    return actividades.sort((a, b) => {
      // Ordenar por fecha y hora
      const fechaA = `${a.diaFecha} ${a.horaInicio || "00:00"}`;
      const fechaB = `${b.diaFecha} ${b.horaInicio || "00:00"}`;
      return fechaA.localeCompare(fechaB);
    });
  }, [dias, currentLang]);

  // Filtrar actividades por tipo
  const actividadesFiltradas = useMemo(() => {
    if (filtroTipo === "todos") return todasActividades;
    return todasActividades.filter((act) => act.tipo === filtroTipo);
  }, [todasActividades, filtroTipo]);

  // Obtener tipos únicos de actividades
  const tiposDisponibles = useMemo(() => {
    const tipos = new Set();
    todasActividades.forEach((act) => {
      if (act.tipo) tipos.add(act.tipo);
    });
    return Array.from(tipos);
  }, [todasActividades]);

  // Obtener actividades agrupadas por día
  const actividadesPorDia = useMemo(() => {
    const agrupadas = {};
    actividadesFiltradas.forEach((act) => {
      const key = act.diaFecha || "sin-fecha";
      if (!agrupadas[key]) {
        agrupadas[key] = {
          fecha: act.diaFecha,
          nombre: act.diaNombre,
          actividades: [],
        };
      }
      agrupadas[key].actividades.push(act);
    });
    return Object.values(agrupadas);
  }, [actividadesFiltradas]);

  // Función para formatear hora
  const formatearHora = (hora) => {
    if (!hora) return "";
    // Si viene en formato HH:mm, devolverlo tal cual
    if (hora.length === 5 && hora.includes(":")) return hora;
    return hora;
  };

  // Función para verificar si una actividad está en curso o es próxima
  const getEstadoActividad = (actividad) => {
    if (!actividad.diaFecha || !actividad.horaInicio) return null;
    
    const ahora = new Date();
    const fechaActividad = new Date(`${actividad.diaFecha}T${actividad.horaInicio}`);
    const fechaFin = actividad.horaFin 
      ? new Date(`${actividad.diaFecha}T${actividad.horaFin}`)
      : null;

    // Si tiene hora fin, verificar si está en curso
    if (fechaFin && ahora >= fechaActividad && ahora <= fechaFin) {
      return "en_curso";
    }
    
    // Si es próxima (dentro de las próximas 2 horas)
    const dosHoras = 2 * 60 * 60 * 1000;
    if (fechaActividad > ahora && fechaActividad <= new Date(ahora.getTime() + dosHoras)) {
      return "proxima";
    }

    return null;
  };

  // Renderizar vista Timeline
  const renderTimeline = () => {
    if (actividadesPorDia.length === 0) {
      return (
        <Paper style={{ padding: "40px", textAlign: "center" }}>
          <Typography variant="body1" color="textSecondary">
            {t("event.agenda.noActivities")}
          </Typography>
        </Paper>
      );
    }

    return actividadesPorDia.map((grupo, grupoIndex) => (
      <Box key={grupoIndex} style={{ marginBottom: "32px" }}>
        <Typography
          variant="h5"
          style={{
            marginBottom: "16px",
            color: primaryColor,
            fontWeight: "bold",
          }}
        >
          {grupo.nombre || grupo.fecha || `Día ${grupoIndex + 1}`}
        </Typography>

        <Box
          style={{
            position: "relative",
            paddingLeft: "40px",
          }}
        >
          {/* Línea vertical de la timeline */}
          <Box
            style={{
              position: "absolute",
              left: "15px",
              top: 0,
              bottom: 0,
              width: "2px",
              backgroundColor: primaryColor,
              opacity: 0.3,
            }}
          />

          {grupo.actividades.map((actividad, actIndex) => {
            const estado = getEstadoActividad(actividad);
            const esEnCurso = estado === "en_curso";
            const esProxima = estado === "proxima";

            return (
              <Box
                key={actIndex}
                style={{
                  position: "relative",
                  marginBottom: "24px",
                }}
              >
                {/* Punto en la timeline */}
                <Box
                  style={{
                    position: "absolute",
                    left: "-32px",
                    top: "8px",
                    width: "16px",
                    height: "16px",
                    borderRadius: "50%",
                    backgroundColor: esEnCurso ? "#ff5722" : esProxima ? "#ff9800" : primaryColor,
                    border: "3px solid #fff",
                    boxShadow: `0 0 0 2px ${primaryColor}`,
                    zIndex: 1,
                  }}
                />

                <Card
                  style={{
                    backgroundColor: esEnCurso ? "rgba(255, 87, 34, 0.05)" : "#fff",
                    boxShadow: esEnCurso ? `0 2px 8px rgba(255, 87, 34, 0.2)` : "0 2px 8px rgba(0,0,0,0.1)",
                    borderRadius: "12px",
                    border: esEnCurso ? `2px solid #ff5722` : esProxima ? `2px solid #ff9800` : "none",
                  }}
                >
                  <CardContent style={{ padding: "16px" }}>
                    <Box style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "12px" }}>
                      <Box style={{ flex: 1 }}>
                        <Box style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px", flexWrap: "wrap" }}>
                          <AccessTimeIcon style={{ fontSize: "18px", color: "#666" }} />
                          <Typography variant="body2" style={{ color: "#666", fontWeight: "bold" }}>
                            {formatearHora(actividad.horaInicio)}
                            {actividad.horaFin && ` - ${formatearHora(actividad.horaFin)}`}
                          </Typography>
                          <Chip
                            label={TIPOS_ACTIVIDAD_LABELS[actividad.tipo] || actividad.tipo}
                            size="small"
                            color={TIPOS_ACTIVIDAD_COLORS[actividad.tipo] || "default"}
                            style={{ fontSize: "0.7rem" }}
                          />
                          {esEnCurso && (
                            <Chip
                              label={t("event.agenda.now")}
                              size="small"
                              style={{
                                backgroundColor: "#ff5722",
                                color: "#fff",
                                fontWeight: "bold",
                              }}
                            />
                          )}
                          {esProxima && (
                            <Chip
                              label={t("event.agenda.next")}
                              size="small"
                              style={{
                                backgroundColor: "#ff9800",
                                color: "#fff",
                                fontWeight: "bold",
                              }}
                            />
                          )}
                        </Box>

                        <Typography
                          variant="h6"
                          style={{
                            marginBottom: "8px",
                            color: estilos.colorTexto || "#333",
                            fontWeight: "bold",
                          }}
                        >
                          {getTranslatedText(actividad.titulo)}
                        </Typography>

                        {actividad.descripcion && getTranslatedText(actividad.descripcion) && (
                          <Typography
                            variant="body2"
                            style={{
                              color: estilos.colorTexto ? "rgba(0, 0, 0, 0.7)" : "#666",
                              marginBottom: "12px",
                            }}
                          >
                            {getTranslatedText(actividad.descripcion)}
                          </Typography>
                        )}

                        {actividad.ubicacion && (
                          <Box style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "8px" }}>
                            <LocationOnIcon style={{ fontSize: "16px", color: "#666" }} />
                            <Typography variant="body2" style={{ color: "#666", fontSize: "0.875rem" }}>
                              {actividad.ubicacion}
                            </Typography>
                          </Box>
                        )}

                        {actividad.ponente?.nombre && (
                          <Box style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "12px", padding: "12px", backgroundColor: "rgba(0,0,0,0.02)", borderRadius: "8px" }}>
                            {actividad.ponente.foto && (
                              <img
                                src={actividad.ponente.foto.startsWith('http') ? actividad.ponente.foto : `${SERVER_URL}/${actividad.ponente.foto}`}
                                alt={actividad.ponente.nombre}
                                style={{
                                  width: "48px",
                                  height: "48px",
                                  borderRadius: "50%",
                                  objectFit: "cover",
                                }}
                                onError={(e) => {
                                  e.target.style.display = "none";
                                }}
                              />
                            )}
                            <Box style={{ flex: 1 }}>
                              <Box style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                <PersonIcon style={{ fontSize: "16px", color: "#666" }} />
                                <Typography variant="subtitle2" style={{ fontWeight: "bold" }}>
                                  {actividad.ponente.nombre}
                                </Typography>
                              </Box>
                              {actividad.ponente.cargo && (
                                <Typography variant="caption" style={{ color: "#666", display: "block", marginTop: "4px" }}>
                                  {actividad.ponente.cargo}
                                </Typography>
                              )}
                              {actividad.ponente.bio && getTranslatedText(actividad.ponente.bio) && (
                                <Typography variant="body2" style={{ color: "#666", fontSize: "0.75rem", marginTop: "4px" }}>
                                  {getTranslatedText(actividad.ponente.bio)}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            );
          })}
        </Box>
      </Box>
    ));
  };

  return (
    <Container maxWidth="md" style={{ padding: "20px 0" }}>
      {/* Filtros */}
      {tiposDisponibles.length > 0 && (
        <Box style={{ marginBottom: "24px", display: "flex", gap: "12px", flexWrap: "wrap", alignItems: "center" }}>
          <FormControl style={{ minWidth: "200px" }}>
            <InputLabel>{t("event.agenda.filterByType")}</InputLabel>
            <Select
              value={filtroTipo}
              onChange={(e) => setFiltroTipo(e.target.value)}
              label={t("event.agenda.filterByType")}
            >
              <MenuItem value="todos">{t("event.agenda.all")}</MenuItem>
              {tiposDisponibles.map((tipo) => (
                <MenuItem key={tipo} value={tipo}>
                  {TIPOS_ACTIVIDAD_LABELS[tipo] || tipo}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}

      {/* Contenido de la agenda */}
      {formatoVisualizacion === "timeline" && renderTimeline()}

      {/* Botón de valoración */}
      {contenidoIntermedio?.botonRatings && (
        <Box style={{ marginTop: "40px", textAlign: "center" }}>
          <Button
            variant="contained"
            onClick={onNavigateToRatings}
            style={{
              backgroundColor: contenidoIntermedio.botonRatings.estilo?.colorFondo || primaryColor,
              color: contenidoIntermedio.botonRatings.estilo?.colorTexto || "#fff",
              padding: "12px 32px",
              borderRadius: "25px",
              fontSize: "1rem",
              fontWeight: "bold",
              textTransform: "none",
              boxShadow: "none",
              marginBottom: "20px",
            }}
          >
            {getTranslatedText(contenidoIntermedio.botonRatings.texto) || t("event.agenda.rateEvent")}
          </Button>
        </Box>
      )}
    </Container>
  );
};

export default AgendaContent;

